import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

interface UpdateEmployeeParams {
  userId: string;
  updates: {
    // Sensitive rate columns are routed via admin_set_employee_rates RPC
    hourly_cost_rate?: number | null;
    hourly_sales_rate?: number | null;
    hire_date?: string | null;
    // Non-sensitive columns updated directly on users table
    monthly_capacity_hours?: number | null;
    role?: string;
    specialization?: string | null;
    is_active?: boolean;
    birthday?: string | null;
    job_title?: string | null;
    target_billing_rate?: number | null;
    /** CRM-4.0: kan bookes som vert for kundemøte (admin). */
    can_host_meetings?: boolean;
    /** VIB-2917: avdeling og utstyrsansvarlig (admin). */
    department_id?: string | null;
    can_manage_equipment?: boolean;
  };
}

export function useUpdateEmployee() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, updates }: UpdateEmployeeParams) => {
      // Extract rate columns to route via SECURITY DEFINER RPC
      const { hourly_cost_rate, hourly_sales_rate, hire_date, ...directUpdates } = updates;
      const hasRateChanges =
        hourly_cost_rate !== undefined ||
        hourly_sales_rate !== undefined ||
        hire_date !== undefined;

      // Update non-sensitive columns directly if any
      if (Object.keys(directUpdates).length > 0) {
        const { error } = await supabase
          .from("users")
          .update(directUpdates)
          .eq("id", userId);
        if (error) throw new Error(`Oppdatering feilet: ${error.message}`);
      }

      // Update rate columns via SECURITY DEFINER RPC (admin only)
      if (hasRateChanges) {
        const { error: rpcError } = await supabase.rpc("admin_set_employee_rates", {
          p_user_id: userId,
          p_cost: hourly_cost_rate ?? null,
          p_sales: hourly_sales_rate ?? null,
          p_hire: hire_date ?? null,
        });
        if (rpcError) throw new Error(`Oppdatering av rater feilet: ${rpcError.message}`);
      }
    },
    onSuccess: async (_d, vars) => {
      // invalidateFor("users") covers employee-rates (RPC) via CACHE_REGISTRY
      await invalidateFor(qc, "users", { userId: vars.userId });
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}
